/**
 * Caso de uso "agregar tarjeta nueva": valida → tokeniza con el SDK de Payway → guarda la tarjeta
 * en la caché de sesión. Backend no tiene endpoint de alta: la tarjeta se persiste al completarse
 * la operación (`initiate`), por eso se conserva localmente mientras dure la sesión.
 */
import { NewCardValidationError } from '../domain/errors';
import type { NewCardData, NewCardToken, SavedCard } from '../domain/models';
import type { CardTokenizer } from '../infrastructure/paywayTokenizer';
import type { SessionCardCache } from '../infrastructure/sessionCardCache';
import { validateNewCard } from './newCardValidation';

export interface AddCardDeps {
  tokenizer: CardTokenizer;
  cardCache: SessionCardCache;
}

/**
 * Tarjeta de sesión a partir del token de pago y de los datos ingresados (sin número ni CVV).
 * Banco, medio de pago y categoría no se conocen hasta que backend la guarde: el diseño usa la
 * marca inferida por BIN. Sin `paymentMethodId`: backend lo resuelve con el prefijo de 9 dígitos
 * (`extendedBin`), que es lo único del número que se conserva además de los últimos 4.
 */
const EXTENDED_BIN_LENGTH = 9;

const toExtendedBin = (cardNumber: string): string | undefined => {
  const digits = cardNumber.replace(/\D/g, '');
  return digits.length >= EXTENDED_BIN_LENGTH ? digits.slice(0, EXTENDED_BIN_LENGTH) : undefined;
};

/** Payway suele devolver BIN de 6 dígitos; backend pide 9 para tarjetas nuevas sin `paymentMethodId`. */
const extendedBinFromTokenBin = (bin: string): string | undefined => {
  const digits = bin.replace(/\D/g, '');
  if (digits.length >= EXTENDED_BIN_LENGTH) {
    return digits.slice(0, EXTENDED_BIN_LENGTH);
  }
  if (digits.length === 6) {
    return `${digits}000`;
  }
  return undefined;
};

export const toSessionCard = (
  token: NewCardToken,
  data: NewCardData,
  now: number = Date.now(),
): SavedCard => ({
  token: token.token,
  paymentTokenCreatedAt: now,
  bin: token.bin,
  extendedBin: toExtendedBin(data.cardNumber) ?? extendedBinFromTokenBin(token.bin),
  // Titular tal como lo devolvió el tokenizador; si no lo informa, el ingresado.
  holderName: token.holderName ?? data.holderName.trim(),
  lastFourDigits: token.lastFourDigits,
  expirationMonth: data.expirationMonth,
  expirationYear: data.expirationYear,
  expired: false,
  bank: '',
  paymentMethod: '',
  cardTier: '',
});

export const addCard = async (
  deps: AddCardDeps,
  data: NewCardData,
  now: Date = new Date(),
): Promise<SavedCard> => {
  const invalid = validateNewCard(data, now);
  if (invalid.length > 0) {
    throw new NewCardValidationError(invalid);
  }

  const token = await deps.tokenizer.tokenizeNewCard(data);
  const card = toSessionCard(token, data, now.getTime());
  deps.cardCache.add(card);
  return card;
};
